import { computed, inject, Injectable, signal } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Api } from '../../../core/clients/omni-core/api';
import {
  getVideoTutorialProgress,
  markVideoTutorialSeen,
} from '../../../core/clients/omni-core/functions';
import { HttpError, VideoTutorialProgress } from '../../../core/clients/omni-core/models';
import { ConfigService } from '../../../core/services/config.service';

@Injectable({
  providedIn: 'root',
})
export class VideoTutorialService {
  private omniCore = inject(Api);
  private configService = inject(ConfigService);

  protected static readonly DEMO_TUTORIAL_STORAGE_KEY = 'stromify.demo.videoTutorialProgress';

  #value = signal<VideoTutorialProgress | null>(null);
  #isLoading = signal(false);
  #error = signal<HttpError | Error | unknown | null>(null);

  value = computed(this.#value);
  isLoading = computed(this.#isLoading);
  error = computed(this.#error);

  async load(): Promise<void> {
    this.#value.set(null);
    this.#error.set(null);
    this.#isLoading.set(true);
    if (this.configService.isDemoMode()) {
      this.#value.set(this.loadTutorialProgressFromLocalStorage());
      this.#isLoading.set(false);
      return;
    }

    try {
      const res = await this.omniCore.invoke(getVideoTutorialProgress);
      this.#value.set(res);
    } catch (error) {
      Sentry.captureException(error);
      this.#value.set(null);
      this.#error.set(error);
    } finally {
      this.#isLoading.set(false);
    }
  }

  async loadVideoTutorialProgress(): Promise<void> {
    return this.load();
  }

  async markTutorialAsViewed(id: keyof VideoTutorialProgress): Promise<void> {
    if (this.configService.isDemoMode()) {
      this.saveTutorialProgressToLocalStorage(id);
      await this.load();
      return;
    }

    await this.omniCore.invoke(markVideoTutorialSeen, { id });
    await this.load();
  }

  async markAsViewed(id: keyof VideoTutorialProgress): Promise<void> {
    return this.markTutorialAsViewed(id);
  }

  protected loadTutorialProgressFromLocalStorage(): VideoTutorialProgress {
    const defaultProgress: VideoTutorialProgress = {
      LumoTutorialAnalysis: false,
      LumoTutorialDocuments: false,
      LumoTutorialEnergyInterfaces: false,
      LumoTutorialLocations: false,
      LumoTutorialSupplierContracts: false,
    };

    try {
      const stored = localStorage.getItem(VideoTutorialService.DEMO_TUTORIAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<VideoTutorialProgress>;
        return {
          ...defaultProgress,
          ...parsed,
        };
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    return defaultProgress;
  }

  protected saveTutorialProgressToLocalStorage(id: keyof VideoTutorialProgress): void {
    try {
      const progress = this.loadTutorialProgressFromLocalStorage();
      progress[id] = true;
      localStorage.setItem(
        VideoTutorialService.DEMO_TUTORIAL_STORAGE_KEY,
        JSON.stringify(progress),
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
